import React, { createContext, useCallback, useRef, useContext, useState } from 'react'
import { isEmpty } from 'lodash'

import {
  allTranscriptOptions,
  transcriptDisplayValues,
  REALTIME_TRANSCRIPT,
  POSTPROCESSED_TRANSCRIPT,
  PRETTY_TRANSCRIPT,
} from '@/components/transcript/transcript.constants'

const TranscriptContext = createContext()

// Add/remove a block key without duplicates, returning the same array when
// nothing changes so a repeated report can't churn renders.
const toggleBlockKey = (keys, blockKey, isActive) => {
  if (isActive) {
    return keys.includes(blockKey) ? keys : [...keys, blockKey]
  }
  return keys.includes(blockKey) ? keys.filter((key) => key !== blockKey) : keys
}

const getDefaultTranscriptType = (hasPrettyTranscript, hasPostcallTranscript) => {
  if (hasPrettyTranscript) {
    return PRETTY_TRANSCRIPT
  }
  if (hasPostcallTranscript) {
    return POSTPROCESSED_TRANSCRIPT
  }
  return REALTIME_TRANSCRIPT
}

export const TranscriptProvider = ({
  transcript = [],
  postcallTranscript = [],
  prettyTranscript = null,
  showTranscriptSelector = false,
  comments = null,
  children,
}) => {
  const hasPostcallTranscript = !isEmpty(postcallTranscript)
  const hasPrettyTranscript = !isEmpty(prettyTranscript)
  const transcriptBlockRef = useRef(null)
  const closestTranscriptIndexRef = useRef(0)

  const [isTranscriptVisible, setIsTranscriptVisible] = useState(true)
  const [highlightedTranscriptIndex, setHighlightedTranscriptIndex] = useState(0)
  const [allowAutoScroll, setAllowAutoScroll] = useState(true)
  // Turns with an in-progress comment interaction (selection → pill → composer),
  // keyed by blockIndex. While any is present we freeze playback auto-scroll so
  // the transcript can't slide out from under the user's selection/popover.
  const [commentingBlocks, setCommentingBlocks] = useState([])
  // Turns whose composer is actually open, same keying. A narrower signal than
  // commentingBlocks on purpose: merely selecting text must not touch playback
  // (AVR-875), but an open composer takes the player — pauses it and locks the
  // transport — because a comment is written about one moment of the call and
  // having the audio run on while you type is the whole complaint.
  const [composingBlocks, setComposingBlocks] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [transcriptType, setTranscriptType] = useState(
    getDefaultTranscriptType(hasPrettyTranscript, hasPostcallTranscript)
  )

  const [searchMatches, setSearchMatches] = useState([])
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0)
  const [matchIndicesMap, setMatchIndicesMap] = useState({})
  const [shouldScrollToSearchMatch, setShouldScrollToSearchMatch] = useState(false)

  // Determine the default transcript and transcript options to show a user
  const transcriptSelectorOptions = allTranscriptOptions.filter((option) => {
    if (option.value === PRETTY_TRANSCRIPT) {
      return hasPrettyTranscript
    }
    if (option.value === POSTPROCESSED_TRANSCRIPT) {
      return hasPostcallTranscript
    }
    return true
  })

  const getTranscriptToDisplay = () => {
    const displayValues = transcriptDisplayValues[transcriptType]
    if (displayValues.showPrettyTranscript && hasPrettyTranscript) {
      return prettyTranscript
    }
    if (displayValues.showPostcallTranscript) {
      return postcallTranscript
    }
    return transcript
  }
  const transcriptToDisplay = getTranscriptToDisplay()
  const showTranscriptHypothesis = transcriptDisplayValues[transcriptType].showHypothesis
  const transcriptBlocksFirstTimes = transcriptToDisplay.map(
    ({ timestamp: silenceTimeStamp, data: [{ timestamp }] = [{ timestamp: 0 }] }) =>
      silenceTimeStamp || timestamp
  )

  const handleUpdateHighlightedTranscriptIndex = (value) => {
    setHighlightedTranscriptIndex(value)
  }

  const handleToggleTranscriptVisibility = () => {
    setIsTranscriptVisible(!isTranscriptVisible)
  }

  const handleAllowAutoScrollChange = () => {
    setAllowAutoScroll(!allowAutoScroll)
  }

  // Idempotent add/remove so concurrent per-turn popovers can't leave the freeze
  // stuck on the wrong value regardless of which fires first.
  const setBlockCommenting = useCallback((blockKey, isActive) => {
    setCommentingBlocks((prev) => toggleBlockKey(prev, blockKey, isActive))
  }, [])

  const setBlockComposing = useCallback((blockKey, isActive) => {
    setComposingBlocks((prev) => toggleBlockKey(prev, blockKey, isActive))
  }, [])

  const isCommentingActive = !isEmpty(commentingBlocks)
  const isComposingComment = !isEmpty(composingBlocks)

  const handleTranscriptSelectionChange = (value) => {
    setTranscriptType(value)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setSearchMatches([])
    setCurrentSearchIndex(-1)
    setMatchIndicesMap({})
  }

  const handleSearchTranscript = (event) => {
    const query = event.target.value.toLowerCase()
    setSearchQuery(query)

    if (!query) {
      handleClearSearch()
      return
    }

    const matches = []
    const newMatchIndicesMap = {}

    transcriptToDisplay.forEach((block, blockIndex) => {
      if (block.type !== 'conversation' || !block.data) return

      const fullText = block.data
        .map((s) => s.text)
        .join(' ')
        .toLowerCase()

      newMatchIndicesMap[blockIndex] = []

      let startPos = 0
      let localMatchIndex = 0
      let matchPosition = fullText.indexOf(query, startPos)

      while (matchPosition !== -1) {
        matches.push(matches.length)

        newMatchIndicesMap[blockIndex][localMatchIndex] = matches.length - 1
        localMatchIndex++
        startPos = matchPosition + 1

        matchPosition = fullText.indexOf(query, startPos)
      }
    })

    setSearchMatches(matches)
    setMatchIndicesMap(newMatchIndicesMap)
    setCurrentSearchIndex(isEmpty(matches) ? -1 : 0)
  }

  const handleNextSearchMatch = () => {
    if (isEmpty(searchMatches)) return
    setCurrentSearchIndex((prev) => (prev + 1) % searchMatches.length)
    setShouldScrollToSearchMatch(true)
  }

  const handlePreviousSearchMatch = () => {
    if (isEmpty(searchMatches)) return
    setCurrentSearchIndex((prev) => (prev - 1 + searchMatches.length) % searchMatches.length)
    setShouldScrollToSearchMatch(true)
  }

  const scrollToCurrentSearchMatch = () => {
    if (
      transcriptBlockRef.current &&
      Array.isArray(searchMatches) &&
      !isEmpty(searchMatches) &&
      searchMatches[currentSearchIndex] != null
    ) {
      const blockIndex = Object.keys(matchIndicesMap).find((blockIdx) => {
        const matchIndices = matchIndicesMap[blockIdx] || []
        return matchIndices.some((idx) => idx === searchMatches[currentSearchIndex])
      })

      if (blockIndex) {
        const blockEl =
          transcriptBlockRef.current.querySelectorAll('.transcript-scroll-top')[blockIndex]
        if (blockEl) {
          blockEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    }
    setShouldScrollToSearchMatch(false)
  }

  return (
    <TranscriptContext.Provider
      value={{
        transcript,
        postcallTranscript,
        prettyTranscript,
        showTranscriptSelector,
        transcriptToDisplay,
        transcriptSelectorOptions,
        transcriptBlockRef,
        showTranscriptHypothesis,
        closestTranscriptIndexRef,
        transcriptBlocksFirstTimes,
        transcriptType,
        searchQuery,
        isTranscriptVisible,
        allowAutoScroll,
        isCommentingActive,
        setBlockCommenting,
        isComposingComment,
        setBlockComposing,
        highlightedTranscriptIndex,
        handleUpdateHighlightedTranscriptIndex,
        handleAllowAutoScrollChange,
        handleTranscriptSelectionChange,
        handleToggleTranscriptVisibility,
        handleSearchTranscript,
        handleNextSearchMatch,
        handlePreviousSearchMatch,
        handleClearSearch,
        scrollToCurrentSearchMatch,
        searchMatches,
        currentSearchIndex,
        matchIndicesMap,
        shouldScrollToSearchMatch,
        // Optional voice-agent transcript-comment config (callId, items,
        // handlers). Null on every transcript that isn't wired for commenting.
        comments,
      }}
    >
      {children}
    </TranscriptContext.Provider>
  )
}

export const useTranscript = () => {
  return useContext(TranscriptContext)
}
